import { ref, type Ref, watch, onMounted, onBeforeUnmount, unref, isRef, toRaw } from 'vue'

type Role = 'provider' | 'client'

type LinkOp =
  | 'hello'
  | 'ack'
  | 'request-init'
  | 'init'
  | 'update'
  | 'event'
  | 'bye'

interface LinkMessage<T = unknown> {
  namespace: 'se-link'
  name: string
  channelId: string
  op: LinkOp
  ts?: number
  payload?: T
}

export interface SharedLinkEvent<TPayload = unknown> {
  name: string
  payload?: TPayload
}

export interface SharedLinkProviderOptions<T> {
  name: string
  iframeRef: Ref<HTMLIFrameElement | null>
  mode?: 'providerToClient' | 'bidirectional'
  targetOrigin: string | Ref<string>
  channelId?: string
  debounceMs?: number
  win?: Window
  initial?: Ref<T> | T
  debug?: boolean
}

export interface SharedLinkClientOptions<T> {
  name: string
  default?: Ref<T> | T
  mode?: 'providerToClient' | 'bidirectional'
  targetOrigin: string | Ref<string>
  channelId?: string
  debounceMs?: number
  win?: Window
  debug?: boolean
}

export interface UseSharedLinkReturn<T> {
  data: Ref<T>
  connected: Ref<boolean>
  role: Role
  partnerReady: Ref<boolean>
  send: (value?: T) => void
  emitEvent: <TPayload = unknown>(name: string, payload?: TPayload) => void
  onEvent: <TPayload = unknown>(name: string, handler: (payload: TPayload | undefined, message: LinkMessage<SharedLinkEvent<TPayload>>) => void) => () => void
  dispose: () => void
}

const NAMESPACE: LinkMessage['namespace'] = 'se-link'

export function useSharedLink<T>(options: SharedLinkProviderOptions<T>): UseSharedLinkReturn<T>
export function useSharedLink<T>(options: SharedLinkClientOptions<T>): UseSharedLinkReturn<T>
export function useSharedLink<T>(options: any): UseSharedLinkReturn<T> {
  const isBrowser = typeof window !== 'undefined'
  const providedWin: Window | undefined = options.win
  const w: Window | undefined = providedWin ?? (isBrowser ? window : undefined)

  const role: Role = options.iframeRef ? 'provider' : 'client'
  const name: string = options.name
  const channelId: string = options.channelId ?? name
  const targetOriginSource: string | Ref<string> | undefined = options.targetOrigin
  const currentTargetOrigin = (): string | undefined => {
    const value = unref(targetOriginSource)
    return typeof value === 'string' ? value : undefined
  }
  const isExplicitTargetOrigin = (origin: string | undefined): origin is string => {
    if (typeof origin !== 'string' || !origin || origin === '*') return false
    try {
      const parsed = new URL(origin)
      return ['http:', 'https:'].includes(parsed.protocol) && parsed.origin === origin
    } catch {
      return false
    }
  }
  const mode: 'providerToClient' | 'bidirectional' = options.mode ?? 'providerToClient'
  const debounceMs: number | undefined = options.debounceMs
  const debug: boolean = options.debug ?? true

  const prefix = `[useSharedLink][${name}:${channelId}][${role}]`
  const log = (...args: unknown[]) => { if (debug) console.log(prefix, ...args) }
  const warn = (...args: unknown[]) => { if (debug) console.warn(prefix, ...args) }
  const info = (...args: unknown[]) => { if (debug) console.info(prefix, ...args) }

  const connected = ref<boolean>(false)
  const partnerReady = ref<boolean>(false)

  const initialValue = role === 'provider' ? options.initial : options.default
  const data = ref<T>(
    isRef(initialValue) ? (unref(initialValue) as T) : (initialValue as T)
  ) as Ref<T>
  const safeClone = <U,>(input: U): U => {
    try {
      const source = isRef(input as any) ? unref(input as any) : (input as any)
      const raw = JSON.parse(JSON.stringify(source))
      const sc = (globalThis as any).structuredClone
      if (typeof sc === 'function') {
        return sc(raw)
      }
      return JSON.parse(JSON.stringify(raw)) as U
    } catch (error) {
      warn('safeClone failed; falling back to shallow clone', { error })
      const source = isRef(input as any) ? unref(input as any) : (input as any)
      if (source && typeof source === 'object') {
        return Array.isArray(source) ? ([...source] as unknown as U) : ({ ...(source as any) } as U)
      }
      return source as U
    }
  }

  if (!w) {
    warn('No window available (SSR?). Composable is inert until mounted in browser.')
  }

  log('Initialized', { targetOrigin: currentTargetOrigin(), mode, debounceMs })

  let stopWatches: Array<() => void> = []
  let helloInterval: number | null = null
  let disposeFns: Array<() => void> = []
  let lastSendAt = 0
  let scheduled = false
  let debounceTimer: number | null = null
  let pendingValue: T | undefined
  let applyingRemoteData = false
  const eventHandlers = new Map<string, Set<(payload: unknown, message: LinkMessage<SharedLinkEvent>) => void>>()

  const applyRemoteData = (payload: T) => {
    applyingRemoteData = true
    data.value = payload
    queueMicrotask(() => {
      applyingRemoteData = false
    })
  }

  const getPeerWindow = (): Window | null => {
    if (!w) return null
    if (role === 'provider') {
      const iframeRef: Ref<HTMLIFrameElement | null> = options.iframeRef
      const target = iframeRef?.value?.contentWindow ?? null
      if (!target) warn('Peer window not available yet (iframe not ready)')
      return target
    } else {
      try {
        const target = (w as Window).parent ?? null
        if (!target) warn('parent window not available')
        return target
      } catch {
        warn('Accessing parent window threw, likely cross-origin restrictions')
        return null
      }
    }
  }

  const isForThisChannel = (msg: any): msg is LinkMessage<T> => {
    return (
      msg &&
      msg.namespace === NAMESPACE &&
      msg.name === name &&
      msg.channelId === channelId
    )
  }

  const post = (op: LinkOp, payload?: T) => {
    const targetOrigin = currentTargetOrigin()
    if (!isExplicitTargetOrigin(targetOrigin)) {
      warn('post skipped: an explicit targetOrigin is required', { op })
      return
    }
    const peer = getPeerWindow()
    if (!peer) {
      warn('post skipped: no peer window', { op })
      return
    }
    const clonedPayload = typeof payload === 'undefined' ? undefined : safeClone<T>(payload as T)
    const message: LinkMessage<T> = {
      namespace: NAMESPACE,
      name,
      channelId,
      op,
      ts: Date.now(),
      payload: clonedPayload as T,
    }
    try {
      peer.postMessage(message, targetOrigin)
      log('→ post', { op, targetOrigin, hasPayload: typeof payload !== 'undefined' })
    } catch (error) {
      warn('postMessage failed', { op, payload, error })
    }
  }

  const emitEvent = <TPayload = unknown>(eventName: string, payload?: TPayload) => {
    post('event', {
      name: eventName,
      payload,
    } as unknown as T)
  }

  const onEvent = <TPayload = unknown>(
    eventName: string,
    handler: (payload: TPayload | undefined, message: LinkMessage<SharedLinkEvent<TPayload>>) => void,
  ) => {
    const handlers = eventHandlers.get(eventName) ?? new Set()
    handlers.add(handler as (payload: unknown, message: LinkMessage<SharedLinkEvent>) => void)
    eventHandlers.set(eventName, handlers)

    return () => {
      const currentHandlers = eventHandlers.get(eventName)
      if (!currentHandlers) return
      currentHandlers.delete(handler as (payload: unknown, message: LinkMessage<SharedLinkEvent>) => void)
      if (currentHandlers.size === 0) {
        eventHandlers.delete(eventName)
      }
    }
  }

  const send = (value?: T) => {
    log('send', { value });
    pendingValue = value
    const now = Date.now()
    const doSend = () => {
      const nextValue = (pendingValue ?? data.value) as T
      pendingValue = undefined
      post('update', nextValue)
    }
    if (typeof debounceMs === 'number' && debounceMs > 0) {
      if (scheduled) return
      const elapsed = now - lastSendAt
      if (elapsed >= debounceMs) {
        lastSendAt = now
        doSend()
      } else {
        scheduled = true
        const delay = debounceMs - elapsed
        log('debounce schedule update', { inMs: delay })
        debounceTimer = (w as Window)?.setTimeout?.(() => {
          scheduled = false
          debounceTimer = null
          lastSendAt = Date.now()
          doSend()
        }, delay) as unknown as number
      }
    } else {
      lastSendAt = now
      doSend()
    }
  }

  const onMessage = (evt: MessageEvent) => {
    const msg = evt.data
    if (!isForThisChannel(msg)) {
      return
    }
    const peerWindow = getPeerWindow()
    if (!peerWindow || evt.source !== peerWindow) {
      warn('message ignored because it did not come from the current peer window')
      return
    }
    const targetOrigin = currentTargetOrigin()
    if (!isExplicitTargetOrigin(targetOrigin)) {
      warn('message ignored because an explicit targetOrigin is required')
      return
    }
    if (evt.origin !== targetOrigin) {
      warn('message ignored due to origin mismatch', { origin: evt.origin, expected: targetOrigin })
      return
    }

    log('← message', { op: msg.op })

    switch (msg.op) {
      case 'hello': {
        if (role === 'provider') {
          partnerReady.value = true
          info('received hello; sending ack')
          post('ack')
        }
        break
      }
      case 'ack': {
        if (role === 'client') {
          info('received ack; marking connected and requesting init')
          partnerReady.value = true
          connected.value = true
          post('request-init')
        }
        break
      }
      case 'request-init': {
        if (role === 'provider') {
          info('received request-init; sending init with current data')
          // Ensure data mirrors the authoritative provider source before sending
          if (isRef(options.initial)) {
            data.value = unref(options.initial) as T
            post('init', options.initial.value as T)
          } else {
            post('init', data.value as T)
          }
        }
        break
      }
      case 'init': {
        info('received init; replacing data')
        applyRemoteData(msg.payload as T)
        break
      }
      case 'update': {
        if (role === 'provider' && mode === 'providerToClient') {
          warn('ignored update from client because mode is providerToClient')
          break
        }
        info('received update; applying data')
        applyRemoteData(msg.payload as T)
        break
      }
      case 'event': {
        const eventPayload = msg.payload as unknown as SharedLinkEvent
        if (!eventPayload?.name) {
          warn('ignored event without name')
          break
        }

        const handlers = eventHandlers.get(eventPayload.name)
        if (!handlers?.size) {
          log('event received without handlers', { name: eventPayload.name })
          break
        }

        handlers.forEach((handler) => handler(eventPayload.payload, msg as unknown as LinkMessage<SharedLinkEvent>))
        break
      }
      case 'bye': {
        info('received bye; marking disconnected')
        connected.value = false
        partnerReady.value = false
        break
      }
    }
  }

  const startHelloLoop = () => {
    if (role !== 'client' || !w) return
    let attempt = 0

    const sendHello = () => {
      if (connected.value) return
      attempt += 1
      log('sending hello', { attempt })
      post('hello')
      const backoff = Math.min(1000 * Math.pow(2, attempt - 1), 8000)
      log('schedule next hello', { inMs: backoff })
      helloInterval = (w as Window).setTimeout(sendHello, backoff) as unknown as number
    }

    sendHello()
  }

  const stopHelloLoop = () => {
    if (!w) return
    if (helloInterval) {
      (w as Window).clearTimeout(helloInterval)
      helloInterval = null
    }
  }

  const setupWatchers = () => {
    log('setupWatchers', { role, mode })
    if (role === 'provider') {
      if (isRef(options.initial)) {
        log('watching external initial ref for provider updates (deep)')
        stopWatches.push(watch(options.initial, (val) => {
          if (applyingRemoteData) return
          data.value = unref(val) as T
        }, { deep: true }))
      }
      log('watching internal data ref for provider updates (deep)')
      stopWatches.push(watch(data, () => {
        if (applyingRemoteData) return
        send()
      }, { deep: true }))
    } else if (role === 'client' && mode === 'bidirectional') {
      stopWatches.push(watch(data, (val) => {
        if (applyingRemoteData) return
        if (!connected.value) return
        send(val as T)
      }, { deep: true }))
    }
  }

  const addEventListeners = () => {
    if (!w) return
    w.addEventListener('message', onMessage)
    disposeFns.push(() => w.removeEventListener('message', onMessage))
    log('attached message listener')

    if (role === 'client') {
      const doc = (w as Window & { document?: Document }).document
      const onVisibility = () => {
        const isVisible = doc ? (doc as any).visibilityState === 'visible' : true
        if (isVisible && !connected.value) {
          log('page visible; (re)starting hello loop')
          startHelloLoop()
        }
      }
      if (doc) {
        doc.addEventListener('visibilitychange', onVisibility as any)
        disposeFns.push(() => doc.removeEventListener('visibilitychange', onVisibility as any))
        log('attached visibilitychange listener on document')
      } else {
        w.addEventListener('focus', onVisibility as any)
        disposeFns.push(() => w.removeEventListener('focus', onVisibility as any))
        log('attached focus listener on window')
      }
    }

    const onUnload = () => post('bye')
    w.addEventListener('beforeunload', onUnload)
    disposeFns.push(() => w.removeEventListener('beforeunload', onUnload))
    log('attached beforeunload listener')
  }

  const dispose = () => {
    log('disposing: sending bye and removing listeners/watchers')
    post('bye')
    stopHelloLoop()
    if (debounceTimer !== null && w) {
      w.clearTimeout(debounceTimer)
      debounceTimer = null
      scheduled = false
      pendingValue = undefined
    }
    for (const stopWatch of stopWatches) stopWatch()
    stopWatches = []
    for (const fn of disposeFns) fn()
    disposeFns = []
  }

  onMounted(() => {
    if (!w) return
    log('mounted')
    addEventListeners()
    setupWatchers()
    if (role === 'client') {
      startHelloLoop()
    }
  })

  onBeforeUnmount(() => {
    log('beforeUnmount')
    dispose()
  })

  return {
    data,
    connected,
    role,
    partnerReady,
    send,
    emitEvent,
    onEvent,
    dispose,
  }
}
